import { Lock } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * EmptyStateLocked — "No actions configured" placeholder inside a hook stage.
 *
 * Visual language from the Phase 2 mockup `.empty-state`:
 *   - Diagonal-hatch background (repeating-linear-gradient at 135deg)
 *   - Circular lock icon badge
 *   - MONOSPACE uppercase heading
 *   - Small muted description
 *   - Amber "Phase N" pill
 *
 * Intentionally distinct from the generic EmptyState molecule (which uses an
 * IconCircle and is centred on an empty page). EmptyStateLocked is compact
 * and lives inside a stage column in the pipeline canvas.
 */

export interface EmptyStateLockedProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Main heading. */
  heading?: string
  /** Supporting description text below the heading. */
  description?: string
  /** Phase pill label. */
  phaseTag?: string
}

export const EmptyStateLocked = React.forwardRef<HTMLDivElement, EmptyStateLockedProps>(
  (
    { heading = 'No actions configured', description, phaseTag = 'Phase 3', className, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        // Layout: centred column, fills available height
        'flex flex-col items-center justify-center text-center gap-2.5',
        'flex-1 p-4 rounded-md',
        // Dashed border
        'border border-dashed border-ia-border',
        // Diagonal hatch background — matches mockup repeating-linear-gradient
        'bg-[repeating-linear-gradient(135deg,transparent_0,transparent_9px,rgba(255,255,255,0.012)_9px,rgba(255,255,255,0.012)_18px)]',
        className,
      )}
      {...props}
    >
      {/* Lock badge */}
      <div
        aria-hidden="true"
        className={cn(
          'flex h-[26px] w-[26px] items-center justify-center rounded-pill shrink-0',
          'bg-ia-elevated/40 border border-ia-border text-ia-text-muted',
        )}
      >
        <Lock size={12} strokeWidth={1.8} />
      </div>

      {/* Heading */}
      <p className="font-mono text-[10.5px] uppercase tracking-[0.10em] text-ia-text-secondary leading-none">
        {heading}
      </p>

      {/* Description */}
      {description && (
        <p className="text-[10.5px] text-ia-text-muted leading-[1.4] max-w-[200px]">
          {description}
        </p>
      )}

      {/* Phase pill */}
      {phaseTag && (
        <span
          className={cn(
            'font-mono text-[9px] uppercase tracking-[0.12em]',
            'px-[7px] py-[2px] rounded-pill',
            // Amber — matches mockup accent-register / --accent-register
            'text-ia-warning bg-ia-warning/5 border border-ia-warning/20',
          )}
        >
          {phaseTag}
        </span>
      )}
    </div>
  ),
)
EmptyStateLocked.displayName = 'EmptyStateLocked'
